<template>
  <div class="custom-date-picker">
    <v-menu
      ref="menu"
      v-model="menuOpen"
      :close-on-content-click="false"
      :disabled="disabled"
      transition="scale-transition"
      offset-y
      min-width="290px"
    >
      <template v-slot:activator="{ on, attrs }">
        <v-text-field
          :value="dateFormatted"
          :label="label"
          :prepend-inner-icon="prependInnerIcon"
          readonly
          outlined
          :dense="dense"
          :disabled="disabled"
          clearable
          :clear-icon="disabled ? '' : 'mdi-close'"
          @click:clear="clearDate"
          :rules="rules"
          :error-messages="errorMessages"
          v-bind="attrs"
          v-on="disabled ? {} : on"
          class="date-picker-field"
        ></v-text-field>
      </template>
      <v-date-picker
        v-model="dateSelected"
        :locale="'es'"
        :first-day-of-week="1"
        :min="minDateFormatted"
        :max="maxDateFormatted"
        scrollable
        :events="eventDates"
        event-color="red lighten-2"
        :allowed-dates="allowedDates"
        @input="handleDateSelection"
      >
        <v-spacer></v-spacer>
        <v-btn text color="grey" @click="menuOpen = false">
          Cancelar
        </v-btn>
        <v-btn text color="primary" @click="confirmDate">
          OK
        </v-btn>
      </v-date-picker>
    </v-menu>
  </div>
</template>

<script>
import moment from 'moment'
import 'moment-timezone'

export default {
  name: 'CustomDatePicker',

  props: {
    value: {
      type: [String, Number, Date, null],
      default: null
    },
    min: {
      type: [String, Number, Date],
      default: undefined
    },
    max: {
      type: [String, Number, Date],
      default: undefined
    },
    lastMovement: {
      type: Object,
      default: null
    },
    label: {
      type: String,
      default: 'Fecha'
    },
    prependInnerIcon: {
      type: String,
      default: 'mdi-calendar'
    },
    rules: {
      type: Array,
      default: () => []
    },
    errorMessages: {
      type: [Array, String],
      default: ''
    },
    dense: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    minDateFormatted() {
      if (!this.min) return undefined
      if (typeof this.min === 'number') {
        return moment.unix(this.min).tz('Europe/Madrid').format('YYYY-MM-DD')
      }
      return moment(this.min).tz('Europe/Madrid').format('YYYY-MM-DD')
    },
    maxDateFormatted() {
      if (!this.max) return undefined
      if (typeof this.max === 'number') {
        return moment.unix(this.max).tz('Europe/Madrid').format('YYYY-MM-DD')
      }
      return moment(this.max).tz('Europe/Madrid').format('YYYY-MM-DD')
    },
    eventDates() {
      if (!this.lastMovement || !this.lastMovement.datetime) return []
      let start = null
      if (typeof this.lastMovement.datetime === 'number') {
        start = moment.unix(this.lastMovement.datetime).tz('Europe/Madrid')
      } else if (typeof this.lastMovement.datetime === 'string') {
        start = moment(this.lastMovement.datetime).tz('Europe/Madrid')
      } else if (this.lastMovement.datetime instanceof Date) {
        start = moment(this.lastMovement.datetime).tz('Europe/Madrid')
      }
      if (!start || !start.isValid()) return []
      let end = null
      if (this.lastMovement.end_date) {
        if (typeof this.lastMovement.end_date === 'number') {
          end = moment.unix(this.lastMovement.end_date).tz('Europe/Madrid')
        } else if (typeof this.lastMovement.end_date === 'string') {
          end = moment(this.lastMovement.end_date).tz('Europe/Madrid')
        } else if (this.lastMovement.end_date instanceof Date) {
          end = moment(this.lastMovement.end_date).tz('Europe/Madrid')
        }
      }
      if (!end || !end.isValid() || end.isBefore(start)) {
        return [start.format('YYYY-MM-DD')]
      }
      const dates = []
      let current = start.clone()
      while (current.isSameOrBefore(end, 'day')) {
        dates.push(current.format('YYYY-MM-DD'))
        current.add(1, 'day')
      }
      return dates
    },
    allowedDates() {
      const events = this.eventDates
      
      return date => {
        // Si hay fecha mínima, deshabilitar fechas anteriores
        if (this.min) {
          const minDate = moment(this.minDateFormatted)
          const currentDate = moment(date)
          if (currentDate.isBefore(minDate, 'day')) {
            return false
          }
        }
        
        // Si hay fecha máxima, deshabilitar fechas posteriores
        if (this.max) {
          const maxDate = moment(this.maxDateFormatted)
          const currentDate = moment(date)
          if (currentDate.isAfter(maxDate, 'day')) {
            return false
          }
        }
        
        // Deshabilitar fechas en eventDates
        if (events.length && events.includes(date)) {
          return false
        }
        
        return true
      }
    }
  },

  data () {
    return {
      menuOpen: false,
      dateSelected: null,
      dateFormatted: ''
    }
  },

  watch: {
    value: {
      immediate: true,
      handler (newVal) {
        this.initializeFromProp(newVal)
      }
    },

    dateSelected (newVal) {
      if (newVal) {
        this.formatSelectedDate()
      }
    },

    disabled (newVal) {
      if (newVal) {
        this.menuOpen = false
      }
    }
  },

  methods: {
    initializeFromProp (value) {
      if (!value) {
        this.dateSelected = null
        this.dateFormatted = ''
        return
      }

      try {
        let momentDate

        // Manejo de diferentes formatos de entrada
        if (typeof value === 'number') {
          // Asumir timestamp en segundos
          momentDate = moment.unix(value)
        } else if (typeof value === 'string') {
          // Intentar parsear como fecha ISO o formato natural
          momentDate = moment(value)
        } else if (value instanceof Date) {
          // Es un objeto Date
          momentDate = moment(value)
        }

        if (momentDate && momentDate.isValid()) {
          // Actualizar dateSelected en formato YYYY-MM-DD para v-date-picker
          this.dateSelected = momentDate.format('YYYY-MM-DD')

          // Actualizar el texto formateado
          this.dateFormatted = momentDate.format('DD/MM/YYYY')
        } else {
          console.warn('Fecha inválida recibida:', value)
          this.dateSelected = null
          this.dateFormatted = ''
        }
      } catch (error) {
        console.error('Error al inicializar fecha:', error)
        this.dateSelected = null
        this.dateFormatted = ''
      }
    },

    clearDate () {
      if (this.disabled) return

      this.dateSelected = null
      this.dateFormatted = ''
      this.$emit('input', null)
    },

    handleDateSelection () {
      this.formatSelectedDate()
      this.menuOpen = false
      this.emitValue()
    },

    confirmDate () {
      this.formatSelectedDate()
      this.menuOpen = false
      this.emitValue()
    },

    formatSelectedDate () {
      if (!this.dateSelected) {
        this.dateFormatted = ''
        return
      }

      try {
        const formattedDate = moment(this.dateSelected).format('DD/MM/YYYY')
        this.dateFormatted = formattedDate
      } catch (error) {
        console.error('Error al formatear fecha:', error)
        this.dateFormatted = ''
      }
    },

    emitValue () {
      if (!this.dateSelected) {
        this.$emit('input', null)
        return
      }

      try {
        // Emitir en formato ISO para consistencia
        const isoDate = moment(this.dateSelected).format('YYYY-MM-DD')
        this.$emit('input', isoDate)
      } catch (error) {
        console.error('Error al emitir fecha:', error)
        this.$emit('input', null)
      }
    }
  }
}
</script>

<style scoped>
.custom-date-picker {
  width: 100%;
}

/* Estilos para el campo deshabilitado */
.date-picker-field.v-text-field--disabled {
  color: rgba(0, 0, 0, 0.6) !important;
}

.date-picker-field.v-text-field--disabled .v-label {
  color: rgba(0, 0, 0, 0.6) !important;
}

.date-picker-field.v-text-field--disabled .v-icon {
  color: rgba(0, 0, 0, 0.38) !important;
}
</style>
